import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

import { findTerms, hasTerms, type Term } from '../lib/terms';
import { useAuthStore } from '../state/authStore';
import { useTermsStore } from '../state/termsStore';

/**
 * Explains an abbreviation where it stands: point at ДОО, or tap it, and it says what it is.
 *
 * Mounted once for the whole app instead of wrapped around each abbreviation. There are two hundred
 * of them, nearly all inside translated sentences, and a wrapper is a thing somebody has to remember
 * — so the abbreviations that went unexplained would be the newest ones, on the newest screens.
 * This reads the page as rendered, which means a string added next year is covered the day it ships.
 *
 * It does that without touching the DOM, which belongs to React. The marks are drawn with the CSS
 * Custom Highlight API — ranges over existing text nodes, styled by `::highlight()` — and the word
 * under the pointer is found by asking the browser for the caret position there. Nothing is wrapped,
 * inserted or replaced, so there is nothing for a re-render to trip over.
 *
 * What it cannot do is take keyboard focus: there is no element to focus. That is what the "full
 * names" setting is for — it puts the meaning in the text itself, where a keyboard and a screen
 * reader already are — and why TermLink stays a link.
 */

const HIGHLIGHT = 'danak-term';

/** Text the layer leaves alone: not prose, or our own hint explaining itself recursively. */
const IGNORED = 'script, style, textarea, [contenteditable], [data-no-terms], .term-hint';

/** A tap on one of these already means something, and it is not "define this word". */
const INTERACTIVE = 'a, button, input, select, textarea, label, summary, [role="button"], [role="tab"]';

const HOVER_DELAY = 140;
const LEAVE_DELAY = 180;

interface Hit {
  key: string;
  term: Term;
  rect: DOMRect;
}

function readable(node: Node): node is Text {
  return node.nodeType === Node.TEXT_NODE && !node.parentElement?.closest(IGNORED);
}

function caretAt(x: number, y: number): { node: Node; offset: number } | null {
  // The standard one first; `caretRangeFromPoint` is what Safari has instead.
  if (typeof document.caretPositionFromPoint === 'function') {
    const position = document.caretPositionFromPoint(x, y);

    return position && { node: position.offsetNode, offset: position.offset };
  }

  if (typeof document.caretRangeFromPoint === 'function') {
    const range = document.caretRangeFromPoint(x, y);

    return range && { node: range.startContainer, offset: range.startOffset };
  }

  return null;
}

export function termAtPoint(x: number, y: number, language: string): Hit | null {
  const caret = caretAt(x, y);

  if (!caret || !readable(caret.node)) {
    return null;
  }

  const { node, offset } = caret;

  for (const match of findTerms(node.data, language)) {
    if (offset < match.start || offset > match.end) {
      continue;
    }

    const range = document.createRange();
    range.setStart(node, match.start);
    range.setEnd(node, match.end);

    // The caret snaps to the nearest text, so a pointer in the empty half of a line still reports
    // the last word on it. Only the word's own box counts as pointing at the word.
    const rect = [...range.getClientRects()].find(
      (r) => x >= r.left - 2 && x <= r.right + 2 && y >= r.top - 2 && y <= r.bottom + 2,
    );

    if (rect) {
      return { key: match.key, term: match.term, rect };
    }
  }

  return null;
}

/** Draws the dotted underline. Silently absent where the API is — the hint itself still works. */
function useTermMarks(active: boolean, language: string) {
  useEffect(() => {
    if (!active || typeof CSS === 'undefined' || !('highlights' in CSS)) {
      return;
    }

    let frame = 0;

    const mark = () => {
      frame = 0;

      const ranges: Range[] = [];
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) =>
          hasTerms((node as Text).data, language) && readable(node)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT,
      });

      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        for (const match of findTerms((node as Text).data, language)) {
          const range = document.createRange();
          range.setStart(node, match.start);
          range.setEnd(node, match.end);
          ranges.push(range);
        }
      }

      CSS.highlights.set(HIGHLIGHT, new Highlight(...ranges));
    };

    // One pass per frame however many mutations a render produced. Marking changes no DOM, so it
    // cannot feed the observer that scheduled it.
    const schedule = () => {
      frame ||= requestAnimationFrame(mark);
    };

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    schedule();

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      CSS.highlights.delete(HIGHLIGHT);
    };
  }, [active, language]);
}

export function TermHints() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'bg';
  const active = useTermsStore((s) => s.mode) !== 'plain';
  const signedIn = useAuthStore((s) => s.user) !== null;
  const location = useLocation();

  const [hit, setHit] = useState<Hit | null>(null);
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);
  const box = useRef<HTMLDivElement>(null);

  useTermMarks(active, language);

  useEffect(() => {
    if (!active) {
      setHit(null);
      return;
    }

    let timer = 0;

    const later = (run: () => void, delay: number) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(run, delay);
    };

    const show = (next: Hit | null) =>
      setHit((current) => (current?.key === next?.key && current?.rect.top === next?.rect.top ? current : next));

    const onMove = (event: PointerEvent) => {
      // Touch has no hover; its pointermove is a scroll in progress.
      if (event.pointerType !== 'mouse') {
        return;
      }

      // Inside the hint: stay open, so its text can be read and its link reached.
      if (box.current?.contains(event.target as Node)) {
        window.clearTimeout(timer);
        return;
      }

      const next = termAtPoint(event.clientX, event.clientY, language);

      later(() => show(next), next ? HOVER_DELAY : LEAVE_DELAY);
    };

    const onClick = (event: MouseEvent) => {
      const target = event.target as Element;

      if (box.current?.contains(target)) {
        return;
      }

      window.clearTimeout(timer);
      show(target.closest(INTERACTIVE) ? null : termAtPoint(event.clientX, event.clientY, language));
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setHit(null);
      }
    };

    // The hint is pinned to where the word was. Once the page moves, that is somewhere else.
    const onScroll = () => setHit(null);

    document.addEventListener('pointermove', onMove);
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
    };
  }, [active, language]);

  useEffect(() => setHit(null), [location.pathname]);

  // Placed after it has rendered, because where it fits depends on how big it turned out to be:
  // under the word, flipped above it near the bottom of the screen, and never off either side.
  useLayoutEffect(() => {
    if (!hit || !box.current) {
      setPlace(null);
      return;
    }

    // Into the top layer, which is the only place that is above a modal <dialog> — and the day
    // dialog on the calendar is full of abbreviations. A closed popover is `display: none`, so this
    // has to come before the measuring. Where the API is missing the attribute means nothing and
    // the hint is an ordinary fixed box.
    try {
      if (!box.current.matches(':popover-open')) {
        box.current.showPopover();
      }
    } catch {
      /* no Popover API, or no `:popover-open` to ask: already visible either way */
    }

    const margin = 8;
    const { width, height } = box.current.getBoundingClientRect();
    const below = hit.rect.bottom + margin;
    const fitsBelow = below + height <= window.innerHeight - margin;

    setPlace({
      left: Math.max(margin, Math.min(hit.rect.left, window.innerWidth - width - margin)),
      top: fitsBelow ? below : Math.max(margin, hit.rect.top - height - margin),
    });
  }, [hit]);

  if (!hit) {
    return null;
  }

  return (
    <div
      ref={box}
      className="term-hint"
      role="tooltip"
      popover="manual"
      style={{ left: place?.left ?? 0, top: place?.top ?? 0, visibility: place ? 'visible' : 'hidden' }}
    >
      <p className="term-hint__name">
        <strong>{hit.key}</strong> — {hit.term.full}
      </p>
      <p className="term-hint__note">{hit.term.note}</p>
      {/* The glossary is behind the sign-in, so a visitor on the calculator is not offered a link
          that would bounce them to the login page. */}
      {hit.term.help && signedIn && (
        <Link className="term-hint__more" to={`/help#help-${hit.term.help}`} onClick={() => setHit(null)}>
          {t('help.moreInGlossary')}
        </Link>
      )}
    </div>
  );
}
